import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject, isDevMode } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HANDLE_404 } from '../../../core/constants/context-tokens';
import { HELP_CONTENT_BASE, HelpManifest, HelpNode, flattenTopics } from '../help.models';

/**
 * Loads the help tree and the pages hanging off it.
 *
 * Static files under `public/help-docs`, not an API: the content is part of the build, so there is
 * no backend to be down and no token to attach. Knows nothing about markdown; the routed reader
 * (which is lazy) converts the raw text, so the renderer stays out of the initial bundle.
 */
@Injectable({ providedIn: 'root' })
export class HelpContentService {
  private readonly _http = inject(HttpClient);

  /**
   * The in-flight or settled manifest request. The promise is memoized rather than its value, so
   * two components asking at once share one request.
   */
  private _manifestRequest: Promise<HelpManifest> | null = null;

  /** Pages already fetched, keyed `<lang>/<file>`. */
  private readonly _pages = new Map<string, string>();

  async loadManifest(): Promise<HelpManifest> {
    this._manifestRequest ??= this._fetchManifest();

    try {
      return await this._manifestRequest;
    } catch (error) {
      // Cleared so a failure is retryable.
      this._manifestRequest = null;
      throw error;
    }
  }

  /** One page's markdown. */
  async loadPage(topic: HelpNode): Promise<string> {
    const manifest = await this.loadManifest();
    const key = `${manifest.defaultLang}/${topic.file}`;
    const cached = this._pages.get(key);

    if (cached !== undefined) {
      return cached;
    }

    const markdown = await firstValueFrom(
      this._http.get(`${HELP_CONTENT_BASE}/${key}`, { responseType: 'text', context: quietly() })
    );

    this._pages.set(key, markdown);

    return markdown;
  }

  private async _fetchManifest(): Promise<HelpManifest> {
    const manifest = await firstValueFrom(
      this._http.get<HelpManifest>(`${HELP_CONTENT_BASE}/manifest.json`, { context: quietly() })
    );

    if (isDevMode()) {
      warnAboutDuplicateIds(manifest);
    }

    return manifest;
  }
}

/**
 * Asks the error interceptor not to log a missing help file as an application error.
 * A page that fails to load is reported where the user is looking, in the panel or the reader.
 */
function quietly(): HttpContext {
  return new HttpContext().set(HANDLE_404, true);
}

/** Complains in development when two topics share an id, because the id is the whole route parameter. */
function warnAboutDuplicateIds(manifest: HelpManifest): void {
  const ids = flattenTopics(manifest.nodes).map((topic) => topic.id);
  const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);

  if (duplicates.length > 0) {
    console.warn('help-docs/manifest.json has duplicate topic ids:', [...new Set(duplicates)]);
  }
}
